import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { updateGroup } from '@/api/groups'
import type { Group } from '@/types'

const EMOJIS = ['🎮', '🕹️', '⚔️', '🛡️', '🏆', '💎', '🔥', '⚡', '💀', '👾', '🐉', '🍄', '🎯', '🚀', '🧙', '🏹', '🤖', '🎲', '🦊', '🐺']

interface EmojiPickerModalProps {
  groupId: string
  currentEmoji: string
  onClose: () => void
}

export function EmojiPickerModal({ groupId, currentEmoji, onClose }: EmojiPickerModalProps) {
  const queryClient = useQueryClient()

  const { mutate } = useMutation({
    mutationFn: (emoji: string) => updateGroup(groupId, { emoji }),
    onMutate: async (emoji: string) => {
      await queryClient.cancelQueries({ queryKey: ['groups'] })
      const previous = queryClient.getQueryData<Group[]>(['groups'])
      queryClient.setQueryData<Group[]>(['groups'], (old) =>
        old?.map((g) => (g.id === groupId ? { ...g, emoji } : g)) ?? old
      )
      return { previous }
    },
    onError: (_err, _emoji, context) => {
      if (context?.previous) queryClient.setQueryData(['groups'], context.previous)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] })
    },
  })

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [onClose])

  return createPortal(
    <div className="cg-modal-backdrop" onClick={onClose}>
      <div className="cg-modal" style={{ width: 'auto' }} onClick={(e) => e.stopPropagation()}>
        <div className="cg-modal-header">
          <h2>Change icon</h2>
          <button className="cg-icon-btn cg-modal-close-x" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '14px 18px 18px', display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {EMOJIS.map((e) => (
            <button
              key={e}
              onClick={() => { onClose(); mutate(e) }}
              style={{
                width: 40,
                height: 40,
                fontSize: 20,
                cursor: 'pointer',
                border: '1px solid',
                borderRadius: 'var(--radius)',
                borderColor: e === currentEmoji ? 'var(--accent)' : 'var(--line)',
                background: e === currentEmoji ? 'oklch(from var(--accent) l c h / 0.15)' : 'var(--bg-2)',
              }}
            >
              {e}
            </button>
          ))}
        </div>
      </div>
    </div>,
    document.body,
  )
}
